import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { Grid, Box, Card, CardContent, IconButton, TextField, Typography, LinearProgress, Tooltip } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Outlet, useLocation } from 'react-router-dom';
import { contextMenu } from 'react-contexify';
import { parseNodeKey, buildNodeKey } from '../utils/nodeId';
import { AddProject } from '../views/addProject';
import { moveProjectApi } from '../services/folder.service';
import { findProject, findProjectFolderPath } from '../helpers/folder.utils';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../store/hooks';
import { setFolderTreeCollapsed } from '../store/resuders/app.reducer';
import { getFolderTreeCollapsed } from '../store/selectors/app.selector';

// Moved imports from Folders.tsx
import FolderTree from '../views/FolderTree';
import { FolderMenus } from '../views/FolderContextMenu';
// import FolderDetailsPanel from '../views/FolderDetailsPanel'; // Not used in original Folders.tsx render
import CreateFolderModal from '../components/CreateFolderModal';
import { useFolderData } from '../hooks/folderData.hooks';
import { useFolderOperations } from '../hooks/folderOperations.hooks';
import { useProjectItems } from '../hooks/projectItems.hooks';
import { useFolderSelectionDetails } from '../hooks/folderSelectionDetails.hooks';
import type { Folder, Project } from '../services/folder.service'; // Import Folder and Project types


// TODO: Determine actual AppBar height and padding
const appBarHeight = 64; // Example value
const containerPaddingTop = 16; // Value corresponding to pt: 2
const containerPaddingBottom = 16; // Value corresponding to pb: 2
// Adjust height calculation to account for pt/px in the container
const calculatedHeight = `calc(100vh - ${appBarHeight}px - ${containerPaddingTop}px - ${containerPaddingBottom}px)`; 

const FolderExplorerLayout = () => {
  const dispatch = useAppDispatch();
  const isTreeCollapsed = useSelector(getFolderTreeCollapsed);

  // Search term for filtering the folder tree
  const [treeFilter, setTreeFilter] = useState('');
  const [showModifiedDates, setShowModifiedDates] = useState(false);

  // State and handlers for creating a new project
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [newProjectParentId, setNewProjectParentId] = useState<number | null>(null);
  // State and handler for renaming a project
  const [renameProjectData, setRenameProjectData] = useState<any | null>(null);

  const location = useLocation();


  // --- Hooks moved from Folders.tsx ---
  const {
    folders,
    loading: foldersLoading,
    refetchFolders,
    selectedNodeIds,
    setSelectedNodeIds,
    handleSelectedItemsChange,
    expandedNodeIds,
    handleExpandedItemsChange,
    renamingNodeId,
    renameValue,
    startRenamingFolder,
    handleRenameChange,
    handleRenameCancel,
  } = useFolderData();

  const {
    allProjects, // All projects with full details, expected to have lastModifiedTime
    allProjectsLoading,
    unsortedProjects,
    loadedTestCases, // Note: This state might need reconsideration based on where test cases are displayed
    loadProjectTestCases,
    clearLoadedTestCasesForProject,
    handleOpenProject,
    handleOpenTestCase,
    handleOpenTestCaseResults,
    handleOpenScheduler,
    handleOpenScheduledTestResults,
    handleOpenTestResults,
    refetchAllProjects,
    refreshProjectTestCases,
    backgroundLoading,
    backgroundLoadingProgress,
  } = useProjectItems({ folders, foldersLoading });

  const setExpandedNodeIdsWrapper = useCallback((ids: string[]) => {
    handleExpandedItemsChange({} as React.SyntheticEvent, ids);
  }, [handleExpandedItemsChange]);

  const {
    isCreatingFolder,
    openCreateFolderModal,
    handleCreateCancel,
    handleCreateSubmit,
    getParentFolderName,
    handleRenameSubmit,
    handleDeleteFolder,
    handleCopyProjects,
    handleCutProjects,
    handlePasteItems,
    handleExportProjects,
    handleDeleteProjects,
    handleRefreshAll,
    projectClipboardItems,
    clipboardMode,
    newFolderParentId,
  } = useFolderOperations({
    folders,
    refetchFolders,
    renamingNodeId,
    renameValue,
    onRenameCancel: handleRenameCancel,
    setSelectedNodeIds,
    selectedNodeIds,
    clearLoadedTestCasesForProject,
    refetchAllProjects,
    setExpandedNodeIds: setExpandedNodeIdsWrapper,
    loadedTestCases,
  });

  const cutProjectIdsContext =
    clipboardMode === 'cut' ? projectClipboardItems.map((i) => i.id) : [];
  const copiedProjectIdsContext =
    clipboardMode === 'copy' ? projectClipboardItems.map((i) => i.id) : [];
  const cutProjectCount = cutProjectIdsContext.length;
  const copiedProjectCount = copiedProjectIdsContext.length;

  // Create an enriched version of folders for display in the tree
  const foldersForTree = useMemo(() => {
    if (!folders || folders.length === 0 || !allProjects || allProjects.length === 0) {
      return folders || []; // Return original or empty if no data to enrich/be enriched
    }

    const allProjectsMap = new Map(allProjects.map(p => [p.testProjectID, p]));

    const enrichFolderList = (folderList: Folder[]): Folder[] => {
      return folderList.map(folder => {
        const enrichedProjects = folder.projects.map(projInFolder => {
          const fullProjectData = allProjectsMap.get(projInFolder.testProjectID);
          // If project in folder lacks lastModifiedTime, but the full data has it, use it.
          if (!projInFolder.lastModifiedTime && fullProjectData?.lastModifiedTime) {
            return { ...projInFolder, lastModifiedTime: fullProjectData.lastModifiedTime };
          }
          return projInFolder;
        });

        return {
          ...folder,
          projects: enrichedProjects,
          folders: folder.folders ? enrichFolderList(folder.folders) : [], // Recurse for subfolders
        };
      });
    };

    return enrichFolderList(folders);
  }, [folders, allProjects]);

  // Update selected and expanded items when navigating directly via URL
  useEffect(() => {
    const path = location.pathname;
    const projectTestCaseMatch = path.match(/^\/folders\/project\/(\d+)\/testcase\/(\d+)/);
    const projectMatch = path.match(/^\/folders\/project\/(\d+)/);

    let selectedId: string | null = null;
    let projectId: number | null = null;

    if (projectTestCaseMatch) {
      projectId = Number(projectTestCaseMatch[1]);
      const testCaseId = Number(projectTestCaseMatch[2]);
      selectedId = buildNodeKey({ type: 'testcase', id: testCaseId });
    } else if (projectMatch) {
      projectId = Number(projectMatch[1]);
      selectedId = buildNodeKey({ type: 'project', id: projectId });
    } else if (path === '/folders') {
      selectedId = 'folder-root';
    }

    if (selectedId) {
      setSelectedNodeIds([selectedId]);
    } else {
      setSelectedNodeIds([]);
    }

    if (projectId !== null) {
      const folderPath = findProjectFolderPath(folders, projectId) || [];
      const idsToExpand = new Set(expandedNodeIds);
      idsToExpand.add('folder-root');
      folderPath.forEach((fid) => idsToExpand.add(buildNodeKey({ type: 'folder', id: fid })));
      idsToExpand.add(buildNodeKey({ type: 'project', id: projectId }));
      handleExpandedItemsChange({} as React.SyntheticEvent, Array.from(idsToExpand));

      if (!loadedTestCases[projectId]?.data && !loadedTestCases[projectId]?.loading) {
        loadProjectTestCases(projectId);
      }
    }
  }, [location.pathname, folders]);

  const handleClickCapture = useCallback((event: React.MouseEvent) => {
    const visibleContextMenu = document.querySelector('.contexify:not([hidden])');
    const clickedInsideMenu = (event.target as Element)?.closest('.contexify');

    if (visibleContextMenu && !clickedInsideMenu) {
      event.stopPropagation();
      contextMenu.hideAll();
    }
  }, []);

  // Handler to open AddProject panel
  const openCreateProject = useCallback((nodeId: string) => {
    let parentId = 0;
    if (nodeId !== 'folder-root') {
      try {
        parentId = parseNodeKey(nodeId).id;
      } catch (e) {
        console.error(`Error parsing node ID for create project: ${nodeId}`, e);
        parentId = 0;
      }
    }
    setNewProjectParentId(parentId);
    setIsCreatingProject(true);
  }, []);

  // Open rename project editor
  const openRenameProject = useCallback((projectId: number, parentId?: number) => {
    // Prefer project details from allProjects (includes channelType)
    let project = allProjects.find((p) => p.testProjectID === projectId);
    if (!project) {
      // Fallback to folder tree or unsorted list
      project = findProject(folders, projectId) || unsortedProjects.find((p) => p.testProjectID === projectId);
    }
    if (project) {
      setRenameProjectData(project);
      setIsCreatingProject(false);
    }
  }, [allProjects, folders, unsortedProjects]);

  // Handler after add/edit project modal is submitted
  const handleProjectModalDone = useCallback(async (projectResult: any) => {
    setIsCreatingProject(false);
    const projectId = projectResult.testProjectID ?? projectResult.id ?? projectResult.data?.testProjectID;
    if (renameProjectData) {
      // Rename flow: simply clear and refresh
      setRenameProjectData(null);
    } else {
      // Create flow: move to chosen folder if set
      if (newProjectParentId && newProjectParentId !== 0 && projectId) {
        try {
          await moveProjectApi(projectId, 0, newProjectParentId);
        } catch (e) {
          console.error('Failed to move newly created project:', e);
        }
      }
      setNewProjectParentId(null);
    }
    // Refresh folder tree and project list
    await refetchFolders(false);
    await refetchAllProjects(false);
  }, [renameProjectData, newProjectParentId, refetchFolders, refetchAllProjects]);

  return (
    // Wrap with React fragment and include Menus/Modal outside the Grid
    <>
      <Grid container sx={{ 
        height: calculatedHeight, 
        pt: 2, // Existing top padding
        pb: 2, // Add bottom padding
        px: 2,
        position: 'relative'
      }}>
        {/* Collapsed Tree Indicator */}
        {isTreeCollapsed && (
          <Box sx={{
            position: 'absolute',
            left: 0,
            top: '16px',
            bottom: '16px',
            width: '4px',
            backgroundColor: '#006A68',
            opacity: 0.6,
            borderRadius: '0 2px 2px 0'
          }}
          />
        )}
        
        {/* Left Pane: Folder Tree - Conditionally Render */} 
        {!isTreeCollapsed && (
          <Grid 
            item 
            xs={3} 
            sx={{
              position: 'relative', // Needed for potential absolute positioning inside
              borderRight: '1px solid rgba(0, 0, 0, 0.12)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              pr: 1
            }}
          >
            {/* Card structure moved from Folders.tsx */}
            {/* Apply handleClickCapture to the div containing the Card */}
            <div onClickCapture={handleClickCapture} style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Card elevation={1} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%' }}>
                {/* Sticky Search Header */}
                <Box sx={{ 
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  backgroundColor: 'background.paper',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  p: 1
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    p: 1,
                    backgroundColor: 'grey.50',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'grey.200'
                  }}>
                    <Tooltip 
                      title={showModifiedDates ? "Hide last modified dates" : "Show last modified dates"} 
                      placement="bottom"
                    >
                      <IconButton
                        size="small"
                        onClick={() => setShowModifiedDates(!showModifiedDates)}
                        sx={{
                          color: showModifiedDates ? 'primary.main' : 'text.secondary',
                          backgroundColor: showModifiedDates ? 'primary.50' : 'transparent',
                          '&:hover': {
                            backgroundColor: showModifiedDates ? 'primary.100' : 'action.hover',
                            color: showModifiedDates ? 'primary.dark' : 'text.primary'
                          },
                          transition: 'all 0.2s ease-in-out',
                          border: showModifiedDates ? '1px solid' : 'none',
                          borderColor: showModifiedDates ? 'primary.200' : 'transparent'
                        }}
                      >
                        <AccessTimeIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <TextField
                        size="small"
                        placeholder="Search folders, projects, and test cases..."
                        value={treeFilter}
                        onChange={(e) => setTreeFilter(e.target.value)}
                        fullWidth
                        margin="none"
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'background.paper',
                            fontSize: '0.875rem',
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'primary.main'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderWidth: '1px'
                            }
                          },
                          '& .MuiOutlinedInput-input': {
                            padding: '8px 12px'
                          }
                        }}
                      />
                    </Box>
                  </Box>
                  {/* Background loading indicator */}
                  {backgroundLoading && (
                    <Box sx={{ 
                      mt: 1, 
                      p: 1,
                      backgroundColor: 'info.50',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'info.200'
                    }}>
                      <Typography variant="caption" color="info.dark" sx={{ display: 'block', mb: 0.5 }}>
                        Loading test cases... ({backgroundLoadingProgress.loaded}/{backgroundLoadingProgress.total} projects)
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={backgroundLoadingProgress.total > 0 ? (backgroundLoadingProgress.loaded / backgroundLoadingProgress.total) * 100 : 0}
                        sx={{ height: 4 }}
                      />
                    </Box>
                  )}
                </Box>
                
                {/* Scrollable Content Area */}
                <CardContent sx={{ 
                  flexGrow: 1, 
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  p: 1, 
                  '&:last-child': { pb: 1 },
                  minWidth: 0
                }}>
                  <Box sx={{
                    flexGrow: 1,
                    overflow: 'auto',
                    minHeight: 0,
                    minWidth: 0
                  }}>
                    <FolderTree
                      folders={foldersForTree}
                      loading={foldersLoading}
                      selectedNodeIds={selectedNodeIds}
                      onSelectedItemsChange={(event, itemIds) => {
                        // Close any open add/edit project pane when selecting a tree item
                        setIsCreatingProject(false);
                        setRenameProjectData(null);
                        handleSelectedItemsChange(event, itemIds);
                      }}
                      renamingNodeId={renamingNodeId}
                      renameValue={renameValue}
                      onRenameChange={handleRenameChange}
                      onRenameCancel={handleRenameCancel}
                      onRenameSubmit={handleRenameSubmit}
                      cutProjectIds={cutProjectIdsContext}
                      expandedNodeIds={expandedNodeIds}
                      onExpandedItemsChange={handleExpandedItemsChange}
                      loadedTestCases={loadedTestCases} // Pass loaded test cases if needed by tree
                      loadProjectTestCases={loadProjectTestCases}
                      unsortedProjects={unsortedProjects}
                      unsortedProjectsLoading={allProjectsLoading}
                      filterText={treeFilter}
                      showModifiedDates={showModifiedDates}
                    />
                  </Box>
                </CardContent>
              </Card>
            </div>
          </Grid>
        )}

        {/* Right Pane: Content Outlet - Adjust width and add button */} 
        <Grid 
          item 
          xs={isTreeCollapsed ? 12 : 9} // Adjust width based on state
          sx={{ 
            position: 'relative', // Needed for positioning the button
            height: '100%', 
            display: 'flex',
            flexDirection: 'column',
            pl: 2, // Keep left padding for content spacing
          }}
        >
          {/* Enhanced Collapse/Expand Button */}
          <Tooltip 
            key={isTreeCollapsed ? 'collapsed' : 'expanded'}
            title={isTreeCollapsed ? "Show folder tree" : "Hide folder tree"} 
            placement="right"
          >
            <IconButton
              onClick={() => dispatch(setFolderTreeCollapsed(!isTreeCollapsed))}
              size="medium"
              sx={{
                position: 'absolute',
                top: '-18px', // Position so button center aligns with top of folder tree
                left: isTreeCollapsed ? '8px' : '-24px', 
                zIndex: 1000,
                width: '36px',
                height: '36px',
                backgroundColor: isTreeCollapsed ? '#006A68' : 'background.paper',
                color: isTreeCollapsed ? 'white' : '#006A68',
                border: '2px solid',
                borderColor: '#006A68',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                '&:hover': {
                  backgroundColor: isTreeCollapsed ? '#004A48' : '#CCE8E6',
                }
              }}
              aria-label={isTreeCollapsed ? "Show folder tree" : "Hide folder tree"}
            >
              {isTreeCollapsed ? 
                <ChevronRightIcon sx={{ fontSize: '24px' }} /> : 
                <ChevronLeftIcon sx={{ fontSize: '24px' }} />
              }
            </IconButton>
          </Tooltip>

          {/* The actual content */}
          {(isCreatingProject || renameProjectData) ? (
            <AddProject data={renameProjectData ?? undefined} onDone={handleProjectModalDone} />
          ) : (
            <Outlet />
          )}
        </Grid>
      </Grid>

      {/* Menus and Modal moved from Folders.tsx */}
      <FolderMenus
        onNewFolder={openCreateFolderModal}
        onCreateProject={openCreateProject}
        onRenameProject={openRenameProject}
        onRenameFolder={startRenamingFolder}
        onDeleteFolder={handleDeleteFolder}
        onCopyProjects={handleCopyProjects}
        onCutProjects={handleCutProjects}
        onExportProjects={handleExportProjects}
        onDeleteProjects={handleDeleteProjects}
        onPasteProject={handlePasteItems}
        onOpenProject={handleOpenProject}
        onOpenTestCase={handleOpenTestCase}
        onOpenTestCaseResults={handleOpenTestCaseResults}
        onOpenScheduler={handleOpenScheduler}
        onOpenScheduledTestResults={handleOpenScheduledTestResults}
        onOpenTestResults={handleOpenTestResults}
        onRefreshAll={handleRefreshAll}
        onRefreshProjectTestCases={refreshProjectTestCases}
        cutProjectCount={cutProjectCount}
        copiedProjectCount={copiedProjectCount}
      />
      <CreateFolderModal
        open={isCreatingFolder}
        onClose={handleCreateCancel}
        onSubmit={handleCreateSubmit}
        parentName={getParentFolderName(newFolderParentId ?? null)}
      />
    </>
  );
};

export default FolderExplorerLayout; 